import { useState, useEffect } from 'react';
import { Layout } from '@/components/common/Layout/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PageBody, PageHeader, PageShell } from '@/components/ui/page-shell';
import { MatterCombobox } from '@/components/common/EntityCombobox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, FileText, Download, MoreHorizontal } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { DatePicker } from '@/components/ui/date-picker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/ui/status-badge';
import { TableSkeletonRows, TableEmptyRow } from '@/components/ui/table-states';
import { formatCurrency } from '@/utils/document';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { useDateFormat } from '@/lib/dates';
import api, { showErrorToast } from '@/lib/api';
import { useStore } from '@/lib/store';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';

// Interface for Expense model
interface Expense {
  id: number;
  date: string;
  matter: number;
  firm: number;
  category: string;
  description: string;
  notes?: string;
  amount: number;
  billable: boolean;
  invoiced: boolean;
  created_at: string;
  updated_at: string;
  proof?: string;
}

// Interface for form data
interface ExpenseFormData {
  date: string;
  matter: number;
  category: string;
  description: string;
  notes?: string;
  amount: number;
  billable: boolean;
  invoiced: boolean;
  proof?: File | null;
}

export function Expenses() {
  const { matters, fetchMatters } = useStore();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showInvoiced, setShowInvoiced] = useState(false);
  const { toast } = useToast();
  const { format: formatDisplayDate } = useDateFormat();

  // Common categories for expenses
  const expenseCategories = [
    'Travel',
    'Accommodation',
    'Meals',
    'Supplies',
    'Filing Fees',
    'Software',
    'Postage',
    'Research',
    'Printing',
    'Consultation',
    'Other'
  ];

  // Initial form data for creating a new expense
  const initialFormData: ExpenseFormData = {
    date: format(new Date(), 'yyyy-MM-dd'),
    matter: 0,
    category: '',
    description: '',
    notes: '',
    amount: 0,
    billable: true,
    invoiced: false,
    proof: null
  };

  const [formData, setFormData] = useState<ExpenseFormData>(initialFormData);
  const [proofFile, setProofFile] = useState<File | null>(null);

  // Fetch expenses from API
  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      // Filtering/search below is client-side, so fetch beyond the default
      // page size — otherwise only the newest 50 expenses are ever visible.
      const response = await api.get(`${ROUTES.API.COSTS}?limit=9999`);
      setExpenses(response.data.results);
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to fetch expenses',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission for creating/editing expenses
const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    // Prepare form data including file if present
    const data = new FormData();
    
    // Add all form fields to FormData
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== 'proof') {
        data.append(key, value.toString());
      }
    });
    
    // Add proof file if it exists
    if (proofFile) {
      data.append('proof', proofFile);
    }

    // Add firm ID
    data.append('firm', user?.firms[0]?.toString() || '1');

    if (selectedExpense) {
      // Remove explicit Content-Type header to let Axios set it automatically
      await api.put(`${ROUTES.API.COSTS}${selectedExpense.id}/`, data);
      toast({
        title: 'Success',
        description: 'Expense updated successfully',
      });
    } else {
      await api.post(ROUTES.API.COSTS, data);
      toast({
        title: 'Success',
        description: 'Expense created successfully',
      });
    }
    setIsDialogOpen(false);
    fetchExpenses();
  } catch (error) {
    showErrorToast(error, 'Error', selectedExpense ? 'Failed to update expense' : 'Failed to create expense');
  } finally {
    setIsLoading(false);
  }
};

  // Handle expense deletion
  const handleDelete = async (id: number) => {
    if (confirm('Are you sure you want to delete this expense?')) {
      try {
        await api.delete(`${ROUTES.API.COSTS}${id}/`);
        toast({
          title: 'Success',
          description: 'Expense deleted successfully',
        });
        fetchExpenses();
      } catch (error) {
        showErrorToast(error, 'Error', 'Failed to delete expense');
      }
    }
  };

  // Handle file input change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setProofFile(e.target.files[0]);
    }
  };

  // Download proof file
  const downloadProof = (proofUrl: string) => {
    window.open(proofUrl, '_blank');
  };

  // Initialize data when component mounts
  useEffect(() => {
    fetchExpenses();
    fetchMatters();
  }, []);

  // Filter expenses based on search query and showing invoiced
  const filteredExpenses = expenses.filter(expense => {
    const searchLower = searchQuery.toLowerCase();
    
    // Handle invoiced filter
    if (!showInvoiced && expense.invoiced) {
      return false;
    }
    
    // Search by category, description, notes, amount
    return (
      expense.category.toLowerCase().includes(searchLower) ||
      expense.description.toLowerCase().includes(searchLower) ||
      (expense.notes && expense.notes.toLowerCase().includes(searchLower)) ||
      expense.amount.toString().includes(searchLower)
    );
  });

  // Function to get matter name from ID
  const getMatterName = (matterId: number) => {
    const matter = matters.find(m => m.id === matterId);
    if (!matter) return 'Unknown Matter';
    return matter.group_name ? `${matter.group_name} - ${matter.name}` : matter.name;
  };

  return (
    <Layout>
      <PageShell>
        <PageHeader
          title="Expenses"
          description="Client-billable costs and out-of-pocket items."
          actions={
            <Button
              onClick={() => {
                setSelectedExpense(null);
                setFormData(initialFormData);
                setProofFile(null);
                setIsDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add expense
            </Button>
          }
        />
        <PageBody>
        <div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogContent className="sm:max-w-[625px]">
              <DialogHeader>
                <DialogTitle>
                  {selectedExpense ? 'Edit Expense' : 'Add New Expense'}
                </DialogTitle>
                <DialogDescription>
                  {selectedExpense ? 'Update expense details' : 'Enter expense details below'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="date">Date</Label>
                    <DatePicker
                      id="date"
                      value={formData.date}
                      onChange={(iso) => {
                        if (iso) {
                          setFormData({ ...formData, date: iso });
                        }
                      }}
                      clearable={false}
                      placeholder="Pick a date"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="matter">Matter</Label>
                    <MatterCombobox
                      value={formData.matter || null}
                      onChange={(id) => setFormData({ ...formData, matter: id ?? 0 })}
                      noneLabel="No Matter"
                      placeholder="Select a matter"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                      required
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {expenseCategories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="amount">Amount</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                      required
                    />
                  </div>
                  
                  <div className="grid gap-2 col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid gap-2 col-span-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid gap-2 col-span-2">
                    <Label htmlFor="proof">Proof Document</Label>
                    <Input
                      id="proof"
                      type="file"
                      onChange={handleFileChange}
                    />
                    {selectedExpense?.proof && (
                      <div className="text-sm text-muted-foreground">
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="ml-2"
                          onClick={() => downloadProof(selectedExpense.proof!)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Label htmlFor="billable">Billable</Label>
                    <Switch
                      id="billable"
                      checked={formData.billable}
                      onCheckedChange={(checked) => setFormData({ ...formData, billable: checked })}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Label htmlFor="invoiced">Invoiced</Label>
                    <Switch
                      id="invoiced"
                      checked={formData.invoiced}
                      onCheckedChange={(checked) => setFormData({ ...formData, invoiced: checked })}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : selectedExpense ? 'Update Expense' : 'Create Expense'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <SearchInput
                placeholder="Search expenses..."
                className="min-w-64 flex-1"
                value={searchQuery}
                onChange={setSearchQuery}
              />
              <div className="flex items-center space-x-2">
                <Label htmlFor="show-invoiced" className="text-sm font-medium">
                  Show Invoiced
                </Label>
                <Switch
                  id="show-invoiced"
                  checked={showInvoiced}
                  onCheckedChange={setShowInvoiced}
                />
              </div>
            </div>

            <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Matter</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Proof</TableHead>
                  <TableHead className="w-16 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableSkeletonRows colSpan={8} />
                ) : filteredExpenses.length === 0 ? (
                  <TableEmptyRow colSpan={8}>
                    {searchQuery
                      ? 'No expenses match your search.'
                      : 'No expenses yet. Add an expense to get started.'}
                  </TableEmptyRow>
                ) : (
                  filteredExpenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>{formatDisplayDate(expense.date)}</TableCell>
                      <TableCell>{expense.matter ? getMatterName(expense.matter) : 'No Matter'}</TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell>
                        <div className="font-medium">{expense.description}</div>
                        {expense.notes && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {expense.notes}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(expense.amount)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <StatusBadge status={expense.billable ? 'billable' : 'non_billable'} />
                          {expense.invoiced && <StatusBadge status="invoiced" />}
                        </div>
                      </TableCell>
                      <TableCell>
                        {expense.proof ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => downloadProof(expense.proof!)}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-xs">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" title="Expense actions">
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Actions</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedExpense(expense);
                                setFormData({
                                  ...initialFormData,
                                  date: format(new Date(expense.date), 'yyyy-MM-dd'),
                                  matter: expense.matter,
                                  category: expense.category,
                                  description: expense.description,
                                  notes: expense.notes || '',
                                  amount: expense.amount,
                                  billable: expense.billable,
                                  invoiced: expense.invoiced,
                                });
                                setProofFile(null);
                                setIsDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit expense
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(expense.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
        </PageBody>
      </PageShell>
    </Layout>
  );
}